import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RoleBadge } from "@/components/common/Badges";
import { ArrowRight, Loader2, ShieldCheck, Globe2, Store, Warehouse } from "lucide-react";

const ICONS = { admin: ShieldCheck, regional_manager: Globe2, store_user: Store, warehouse: Warehouse };
const PW = { admin: "EPadmin2026!", regional_manager: "Demo1234!", store_user: "Demo1234!", warehouse: "Demo1234!" };

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [demo, setDemo] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/auth/demo-users").then((r) => setDemo(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) nav("/"); }, [user, nav]);

  const doLogin = async (e, p, key) => {
    setBusy(key);
    try { const u = await login(e, p); toast.success(`Welcome back, ${u.name}`); nav("/"); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white lg:grid lg:grid-cols-[1.1fr_1fr]">
      <section className="relative flex flex-col justify-between overflow-hidden px-8 py-10 sm:px-14 lg:px-16">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-red-600/30 blur-[140px]" />
        <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[380px] rounded-full bg-sky-500/10 blur-[120px]" />
        <div className="relative flex items-center justify-between gap-6">
          <img src="/brand/ep-50.png" alt="EP Group — 50 years of delivering excellence" data-testid="login-ep-logo" className="h-12 w-auto sm:h-14" />
          <img src="/brand/primark-blue.svg" alt="Primark" data-testid="login-primark-logo" className="h-14 w-14 rounded-xl shadow-lg sm:h-16 sm:w-16" />
        </div>
        <div className="relative my-12 max-w-xl">
          <p className="mb-4 inline-flex rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-slate-300">Primark · 60 stores · 8 DCs · 4 regions</p>
          <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Primark <span className="text-red-500">Replenishment</span> Management System
          </h1>
          <p className="mt-6 text-base text-slate-300 sm:text-lg">One portal for stores, regional managers and warehouses: multi-store ordering, approvals, live stock and forecasting — priced in your local currency.</p>
        </div>
        <div className="relative">
          <div className="grid grid-cols-3 gap-4 text-sm">
            {[["£ € $", "Local currency pricing"], ["4-step", "Bulk order wizard"], ["Live", "Stock & approvals"]].map(([a, b]) => (
              <div key={b} className="rounded-xl border border-white/10 bg-white/5 p-4 backdrop-blur"><p className="font-display text-xl font-extrabold">{a}</p><p className="text-xs text-slate-400">{b}</p></div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3 text-xs text-slate-400"><img src="/brand/ep-logo-white.png" alt="EP Group — make it easy" className="h-7 w-auto opacity-90" /><span>© {new Date().getFullYear()} EP Group · epgroup.co.uk</span></div>
        </div>
      </section>

      <section className="flex items-center bg-white px-6 py-10 text-slate-900 sm:px-12 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <h2 className="font-display text-2xl font-extrabold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-slate-500">Pick a demo role for an instant tour, or use your credentials.</p>

          <div className="mt-6 grid gap-2.5" data-testid="demo-login-list">
            {demo.map((d) => {
              const Icon = ICONS[d.role] || ShieldCheck;
              return (
                <button key={d.email} data-testid={`demo-login-${d.role}`} disabled={!!busy}
                  onClick={() => doLogin(d.email, PW[d.role], d.role)}
                  className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-3.5 text-left shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:border-red-300 hover:shadow-md active:scale-[0.99] disabled:opacity-60">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white group-hover:bg-red-600 transition-colors"><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2"><span className="truncate text-sm font-semibold">{d.title}</span><RoleBadge role={d.role} /></span>
                    <span className="mt-0.5 block truncate text-xs text-slate-500">{d.description}</span>
                  </span>
                  {busy === d.role ? <Loader2 className="h-4 w-4 animate-spin text-red-600" /> : <ArrowRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-red-600" />}
                </button>
              );
            })}
          </div>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-400"><span className="h-px flex-1 bg-slate-200" />or with email<span className="h-px flex-1 bg-slate-200" /></div>

          <form onSubmit={(e) => { e.preventDefault(); doLogin(email, password, "form"); }} className="space-y-3" data-testid="login-form">
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
              className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100" />
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
              className="h-11 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none transition-colors focus:border-red-500 focus:ring-2 focus:ring-red-100" />
            <button data-testid="login-submit-button" disabled={!!busy} className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-red-600 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 active:scale-[0.99] disabled:opacity-60">
              {busy === "form" && <Loader2 className="h-4 w-4 animate-spin" />}Sign in
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
